import React, { Fragment, Component, useState, useEffect } from 'react'
import { Router, Route } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import Polyglot from 'node-polyglot'
import queryString from 'query-string'
import * as UserLocale from 'get-user-locale'
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles'
import styled from '@emotion/styled'
import { createBrowserHistory } from 'history'
import UncaughtApplicationError from './components/UncaughtApplicationError'
import { theme, AnalyticsEvents, ProductSlugs, ProductLabels } from '../constants'
import Downloads from './downloads'
import Header from './components/Header'
import * as Translations from '../translations'
import packageJson from '../../package.json'
import LanguageSelect from './components/LanguageSelect'
import localStorageService from '../services/localStorage'
import userService from '../services/user'
import amplitude from '../analytics/amplitude'

const customTheme = createMuiTheme(theme)
const history = createBrowserHistory()

class App extends Component {
  constructor(props) {
    super(props)

    const languageInfo = getLanguageInfo()

    // Load/Save user params.
    let userData = localStorageService.get('user') || {}
    let queryData = null
    if (window.location.search) {
      queryData = queryStringToObject(window.location.search)
      if (queryData) {
        userData = { ...userData, ...queryData }
        localStorageService.set('user', userData)
      }
    }

    // Product to show is taken from the URL: as the first path segment
    // (/nexus, /playbase, case-insensitive) or, as a fallback, from the
    // persisted `app` value (originally set from the signup query param, so it
    // survives the query being stripped and page reloads). When absent, both
    // products are shown, as before.
    const segments = window.location.pathname.split('/').filter(v => !!v)
    const slug = (segments[0] || '').toLowerCase()
    const productSlug = ProductSlugs[slug] ? slug : undefined
    const routeSegments = productSlug ? segments.slice(1) : segments

    let app
    if (productSlug) {
      app = ProductSlugs[productSlug]
    } else if (typeof userData.app === 'string') {
      app = userData.app
    }

    // Check validation token.
    let validationToken = ''
    const [route, param] = routeSegments
    if (route === 'validation') {
      validationToken = param
    }

    this.state = {
      validationToken,
      language: languageInfo.locale,
      referral: userData.referral,
      app,
      uncaughtError: false
    }

    window.I18n = new Polyglot(languageInfo)

    // Remove any query parameter after reading them, keeping the product prefix
    // in the path so the URL keeps reflecting the product.
    const productPrefix = productSlug ? `/${productSlug}` : ''
    if (window.location.pathname.includes('from-trial')) {
      history.replace(`${productPrefix}/from-trial`)
    } else if (window.location.pathname.includes('accept-invitation')) {
      history.replace(`${productPrefix}/accept-invitation`)
    } else if (window.location.pathname.includes('decline-invitation')) {
      history.replace(`${productPrefix}/decline-invitation`)
    } else {
      history.replace(productPrefix || '/')
    }
  }

  componentDidMount() {
    console.info(`(Downloads) ${packageJson.version}`)

    if (this.state.validationToken) {
      checkToken(this.state.validationToken)
    }
  }

  componentDidCatch(error, info) {
    console.log('Uncaught Error: ', error)
    console.log('Error Info: ', info)
    this.setState({ uncaughtError: true })
  }

  onLanguageChange = locale => {
    // Use the translations of the selected language.
    const translation = Object.values(Translations).find(v => v.locale === locale)
    window.I18n = new Polyglot(translation)

    // Store the language locally and update the state.
    localStorageService.set('language', locale)
    this.setState({ language: locale })
  }

  render() {
    // Product label injected into copy that names the downloadable products.
    // When no product is specified, it falls back to the localized "both" label.
    const products = getProductsLabel(this.state.app)

    return this.state.uncaughtError ? (
      <UncaughtApplicationError />
    ) : (
      <Router history={history}>
        <Fragment>
          <ToastContainerStyles>
            <ToastContainer
              position={toast.POSITION.BOTTOM_RIGHT}
              pauseOnHover={false}
              closeButton={false}
              autoClose={false}
              closeOnClick
              hideProgressBar
            />
            <DismissAll />
          </ToastContainerStyles>
          <MuiThemeProvider theme={customTheme}>
            <Header referral={this.state.referral} />
            <LanguageSelect
              language={this.state.language}
              onLanguageChange={this.onLanguageChange}
            />
            <AppPages>
              <Route
                exact
                path={['/nexus/from-trial', '/playbase/from-trial', '/from-trial']}
                render={props => (
                  <Downloads
                    {...props}
                    fromTrial
                    app={this.state.app}
                    bodyTitle={window.I18n.t('download.bodyTitleFromTrial')}
                    bodyText={window.I18n.t('download.bodyTextFromTrial')}
                    secondBodyText={window.I18n.t('download.thanks', { products })}
                  />
                )}
              />
              <Route
                exact
                path={[
                  '/nexus/accept-invitation',
                  '/playbase/accept-invitation',
                  '/accept-invitation'
                ]}
                render={props => (
                  <Downloads
                    {...props}
                    app={this.state.app}
                    bodyTitle={window.I18n.t('download.bodyTitleFromAcceptInvitation')}
                    bodyText={window.I18n.t('download.bodyTextFromAcceptInvitation')}
                  />
                )}
              />
              <Route
                exact
                path={[
                  '/nexus/decline-invitation',
                  '/playbase/decline-invitation',
                  '/decline-invitation'
                ]}
                render={props => (
                  <Downloads
                    {...props}
                    app={this.state.app}
                    bodyTitle={window.I18n.t('download.bodyTitleFromDeclineInvitation')}
                    bodyText={window.I18n.t('download.bodyTextFromDeclineInvitation', { products })}
                  />
                )}
              />
              <Route
                exact
                path={['/nexus', '/playbase', '/']}
                render={props => (
                  <Downloads
                    {...props}
                    app={this.state.app}
                    bodyTitle={window.I18n.t('download.bodyTitle')}
                    bodyText={window.I18n.t('download.bodyText', { products })}
                  />
                )}
              />
            </AppPages>
          </MuiThemeProvider>
        </Fragment>
      </Router>
    )
  }
}

const AppPages = styled.section`
  position: relative;
  scrollbar-width: none;
  flex: 1;
  padding-top: 80px;
  @media (max-width: 700px) {
    padding-top: 105px;
  }
`

const ToastContainerStyles = styled.div`
  .dismiss-all {
    position: fixed;
    bottom: 10px;
    right: 21px;
    color: ${theme.palette.common.black};
    z-index: 99999;
    padding: 5px 15px;
    border-radius: 0;
    box-shadow:
      0px 2px 4px -1px rgba(0, 0, 0, 0.2),
      0px 4px 5px 0px rgba(0, 0, 0, 0.14),
      0px 1px 10px 0px rgba(0, 0, 0, 0.12);
    font-size: 14px;
    cursor: pointer;
    outline: none;
  }

  .Toastify__toast-container {
    width: unset;
    min-width: 320px;
    max-width: 380px;
    max-height: 100vh;
    padding-top: 35px;
    scrollbar-width: none;
    overflow-y: overlay;
    overflow-x: hidden;
    margin-bottom: 15px;

    &::-webkit-scrollbar: {
      width: 0;
    }

    .Toastify__toast {
      font-size: 14px;
      border-radius: 0;
      box-shadow:
        0px 2px 4px -1px rgba(0, 0, 0, 0.2),
        0px 4px 5px 0px rgba(0, 0, 0, 0.14),
        0px 1px 10px 0px rgba(0, 0, 0, 0.12);

      &.Toastify__toast--success {
        background: ${theme.palette.primary.main};
      }

      &.Toastify__toast--error {
        background: ${theme.palette.error.main};
      }

      &.Toastify__toast--info {
        background: ${theme.palette.common.blue};
      }
    }
  }
`

function DismissAll() {
  const [numberOfToasts, setNumberOfToasts] = useState(0)
  useEffect(() => {
    toast.onChange(numberOfToastDisplayed => {
      setNumberOfToasts(numberOfToastDisplayed)
    })
  }, [])

  return numberOfToasts > 1 ? (
    <div
      className="dismiss-all"
      role="button"
      onClick={() => {
        toast.dismiss()
        setNumberOfToasts(0)
      }}
      tabIndex={-1}
    >
      {window.I18n.t('common.clearAll')}
    </div>
  ) : null
}

function getLanguageInfo() {
  // Read language from local-storage.
  let languageInfo
  const storedLocale = localStorageService.get('language')
  if (storedLocale) {
    languageInfo = Object.values(Translations).find(v => v.locale === storedLocale)
  }

  // If null, read language from navigator settings.
  if (!languageInfo) {
    const userLocales = UserLocale.getUserLocales()
    for (let i = 0; i < userLocales.length; i += 1) {
      languageInfo = Object.values(Translations).find(v => v.locale === userLocales[i])
      if (languageInfo) {
        break
      }
    }
  }

  // If null, read language from URL params.
  if (!languageInfo) {
    const parsed = queryString.parse(window.location.search)
    const locale = parsed.lang || parsed.language
    languageInfo = Object.values(Translations).find(v => v.locale === locale)

    // If there's no language info in local-storage, save it.
    if (languageInfo) {
      localStorageService.set('language', locale)
    }
  }

  // If null, use default language.
  if (!languageInfo) {
    languageInfo = Translations.English
  }

  return languageInfo
}

function getProductsLabel(app) {
  if (ProductLabels[app]) {
    return ProductLabels[app]
  }

  return window.I18n.t('download.bothProducts')
}

function queryStringToObject(query) {
  const decode = val => {
    let decodedVal = decodeURIComponent(val)
    if (decodedVal === 'true') decodedVal = true
    if (decodedVal === 'false') decodedVal = false
    if (decodedVal === 'undefined') decodedVal = undefined
    return decodedVal
  }

  if (query) {
    const result = {}
    const pairs = query.slice(1).split('&')
    pairs.forEach(pair => {
      pair = pair.split('=')
      result[pair[0]] = decode(pair[1] || '')
    })

    return result
  }

  return null
}

async function checkToken(token) {
  try {
    const { data: user } = await userService.verifyEmail(token)
    toast.success(window.I18n.t('verifyEmail.successfully'))
    toast.info(window.I18n.t('verifyEmail.readyToLogin'))
    amplitude.setUser(user)
    amplitude.logEvent(AnalyticsEvents.EMAIL_VERIFIED)
  } catch (error) {
    console.warn('error validating token', error)
    toast.error(window.I18n.t('verifyEmail.unsuccessfully'))
    toast.info(window.I18n.t('verifyEmail.generateLink'))
  }
}

export default App
